import { request } from './request';

const CACHEABLE_CONTENT_GROUPS_TTL_MS = {
  home: 60 * 1000, // 60 секунд
};

const contentCache = new Map();

const getCachedContent = (cacheKey, ttlMs, fetcher) => {
  const now = Date.now();
  const cached = contentCache.get(cacheKey);

  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }

  const promise = fetcher().catch((error) => {
    // не кэшируем ошибочный ответ — следующий запрос попробует сходить в бэкенд заново
    contentCache.delete(cacheKey);

    throw error;
  });

  contentCache.set(cacheKey, { promise, expiresAt: now + ttlMs });

  return promise;
};

export const ApiCall = {
  getGoods({ locale, ctx }) {
    const urls = { local: 'getgoods.json', prod: `/${locale}/users/getgoods/` };

    return request.call_auth('get', urls, {}, { locale, ctx });
  },

  getProfile({ locale, ctx }) {
    const urls = { local: 'profile.json', prod: `/${locale}/users/profile/` };

    return request.call_auth('get', urls, {}, { locale, ctx });
  },

  getPricing({ locale, clientIp }) {
    const urls = { local: 'pricing.json', prod: `/${locale}/getpricing/?format=json` };
    const headers = clientIp ? { 'Pop-X-Real-IP': clientIp } : {};

    return request.call_public('get', urls, undefined, { headers });
  },

  getBlog({ category, page, locale }) {
    const subPage = category ? `${category}/` : '';
    const urls = { local: `blog/${category ? category : 'all'}.json`, prod: `/${locale}/getblog/${subPage}` };

    return request.call_public('get', urls, { page });
  },

  getBlogPage({ url, category, locale }) {
    const subPage = category ? `${category}/` : '';
    const encodedUrl = encodeURIComponent(url);
    const urls = { local: `blog/${encodedUrl}.json`, prod: `/${locale}/getblog/${subPage}${encodedUrl}` };

    return request.call_public('get', urls);
  },

  getPopularBlog({ locale }) {
    const urls = { local: 'blog/all.json', prod: `/${locale}/getblog/` };

    return request.call_public('get', urls);
  },

  setComment({ id, body, locale, ctx }) {
    const urls = { local: 'blog/set-comment.json', prod: `/${locale}/getblog/addcomment/` };
    const settings = { useFormData: true, locale, ctx };

    return request.call_auth('post', urls, { id, body }, settings);
  },

  sendSupportForm({ params, locale }) {
    const urls = { local: 'support_form.json', prod: `/${locale}/feedback/` };
    const settings = { useFormData: true };

    return request.call_public('post', urls, params, settings);
  },

  sendSubscribeForm({ params, locale }) {
    const urls = { local: 'subscribe_form.json', prod: `/${locale}/gallery/subscribe/` };
    const settings = { useFormData: true };

    return request.call_public('post', urls, params, settings);
  },

  getContent({ groups, blocks, locale, getParams = {} }) {
    const local = blocks ? `content/${groups}_${blocks}.json` : `content/${groups}.json`;
    const params = blocks ? { ['blocks[]']: blocks, ...getParams } : { ['groups[]']: groups, ...getParams };
    const urls = { local, prod: `/${locale}/content/` };
    const fetcher = () => request.call_public('get', urls, params);

    const ttlMs = !blocks && Object.keys(getParams).length === 0 && CACHEABLE_CONTENT_GROUPS_TTL_MS[groups];

    if (ttlMs) {
      return getCachedContent(`${groups}:${locale}`, ttlMs, fetcher);
    }

    return fetcher();
  },

  getPreviewScreenshots({ id, locale, isTour, isEstimate }) {
    const proPath = isTour ? `tour` : isEstimate ? 'estimates' : 'screenshot';
    // const urls = { local: 'preview/panorama.json', prod: `/${locale}/view/${proPath}/?id=${id}` };
    const urls = { local: 'preview/estimate.json', prod: `/${locale}/view/${proPath}/?id=${id}` };

    return request.call_public('get', urls);
  },

  getPreviewDesignProject({ id }) {
    const urls = { local: 'preview/designproject.json', prod: '/view/designproject/' };

    return request.call_public('get', urls, { id });
  },

  getGalleryProjectsTags({ locale }) {
    const urls = {
      local: 'gallery/tags.json',
      prod: `/${locale}/content/?blocks[]=gallery_tags_style&blocks[]=gallery_tags_room&blocks[]=gallery_tags_room_style`,
    };

    return request.call_public('get', urls);
  },

  getGalleryProjects({ locale }) {
    const urls = { local: 'gallery/projects.json', prod: `/${locale}/content/?blocks[]=gallery_projects` };

    return request.call_public('get', urls);
  },

  getCreatedProject({ locale, file, title }) {
    const urls = {
      local: 'gallery/create.json',
      prod: `/${locale}/projects/add/?folder_id=0&file=${file}&title=${title}`,
    };

    return request.call_auth('get', urls);
  },

  getLibrary({ locale }) {
    const urls = {
      local: 'library/full-library.json',
      prod: `/${locale}/content/?groups[]=library_getstarted&groups[]=library_faq&groups[]=library_tools&exclude_fields=short_text,text,image,helper_text,helper_hotkey,helper_animation,helper_preview`,
    };

    return request.call_public('get', urls);
  },
  getLibraryCategory({ locale, group }) {
    const urls = {
      local: 'library/category-library.json',
      prod: `/${locale}/content/?groups[]=library_${group}&exclude_fields=short_text,text,image,helper_text,helper_hotkey,helper_animation,helper_preview`,
    };

    return request.call_public('get', urls);
  },
  getLibrarySubCategory({ locale, block }) {
    const urls = {
      local: 'library/subcategory-library.json',
      prod: `/${locale}/content/?blocks[]=${block}&exclude_fields=short_text,image,text,helper_text,helper_hotkey,helper_animation,helper_preview`,
    };

    return request.call_public('get', urls);
  },
  getLibraryArticle({ locale, block, article }) {
    const urls = {
      local: 'library/article.json',
      prod: `/${locale}/content/?blocks[]=${block}&items=${article}&exclude_fields=short_text,image,helper_text,helper_hotkey,helper_animation,helper_preview`,
    };

    return request.call_public('get', urls);
  },

  getHelpSections({ locale }) {
    const urls = { local: 'help/help-center.sections.json', prod: `/${locale}/hc/` };

    return request.call_public('get', urls);
  },

  getHelpSection({ locale, section, category, limit, page }) {
    const urls =
      category.length > 0
        ? {
            local: `help/help-center.${section}.${category}.json`,
            prod: `/${locale}/hc/${section}/${category}/?limit=${limit}&page=${page}`,
          }
        : {
            local: `help/help-center.${section}.json`,
            prod: `/${locale}/hc/${section}/?limit=${limit}&page=${page}`,
          };

    return request.call_public('get', urls);
  },

  getHelpItem({ locale, section, category, article }) {
    const urls = {
      local: article.includes('?course_id')
        ? 'help/help-center.article-content-in-course.json'
        : 'help/help-center.article-content.json',
      prod: `/${locale}/hc/${section}/${category}/${article}`,
    };

    return request.call_public('get', urls);
  },

  getHelpSearch({ locale, query }) {
    const urls = {
      local: 'help/help-center.search.json',
      prod: `/${locale}/hc/?q=${query}`,
    };
    return request.call_public('get', urls);
  },

  checkConnection() {
    return request.call_check_connection();
  },
};
